import { Loader2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  ElectricityScreen,
  LpgScreen,
  PngScreen,
} from '@/features/workflows/components/simple/preview-screens';
import { toast } from '@/lib/notify';

import { useVerifyStep } from '../../api/useVerifyStep';
import type {
  SessionDetailDto,
  SessionStepApiBindingDto,
  SessionStepDto,
} from '../../schemas/sessionSchemas';
import { extractReason } from '../pan/pan.utils';
import { isSessionExpiredError, SessionExpiredScreen } from '../SessionExpiredScreen';
import {
  CHECK_FAILED_TOAST,
  RETRY_EXHAUSTED_NOTE,
  tryAnotherMethodNote,
} from '../stepFallbackNotes';
import { UtilityBillDevSimulator } from './UtilityBillDevSimulator';
import {
  isValidConsumerId,
  isValidLpgId,
  isValidOptionalMobile,
  UTILITY_TYPE_LABEL,
  type UtilityType,
  utilityTypeForBinding,
} from './utility.utils';

// Runs the combined Utility Bill Verification step. Only the PRIMARY (highest-
// priority) enabled connection type is shown — the applicant does NOT pick between
// types. The other enabled types are priority fallbacks: on a technical provider
// failure the runner advances to the next type's form (visible fallback note),
// exactly like the Bank / ID Verification runners. Only the active type's API is
// called (targeted binding).
export function UtilityBillStepRunner({
  session,
  step,
}: {
  session: SessionDetailDto;
  step: SessionStepDto;
}) {
  const { mutate, isPending } = useVerifyStep(session.id);

  // The enabled connection methods (priority order), each mapped to its type.
  const types = useMemo(() => {
    return step.apiBindings
      .slice()
      .sort((a, b) => a.priority - b.priority)
      .map((b) => ({ binding: b, type: utilityTypeForBinding(b.operationId) }))
      .filter(
        (t): t is { binding: SessionStepApiBindingDto; type: UtilityType } => t.type !== null,
      );
  }, [step.apiBindings]);

  const [activeIdx, setActiveIdx] = useState(0);
  const [electricity, setElectricity] = useState({
    consumer_id: '',
    service_provider: '',
    regMobileNo: '',
  });
  const [png, setPng] = useState({ ConsumerId: '', ServiceProvider: '', BpNo: '' });
  const [lpg, setLpg] = useState({ LpgId: '' });
  const [fallbackNote, setFallbackNote] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);

  const active = types[activeIdx] ?? types[0];

  if (expired || session.isExpired) {
    return <SessionExpiredScreen />;
  }

  if (step.status === 'Failed') {
    return <FailureScreen reason={extractReason(step.resultJson)} />;
  }

  if (types.length === 0 || !active) {
    return (
      <p className="text-sm text-[#666666]">No connection methods are enabled for this step.</p>
    );
  }

  // Captured after the guard so the closures below see non-undefined values.
  const activeType = active.type;
  const activeBindingId = active.binding.catalogueEntryId;

  function inputFor(type: UtilityType): { input: Record<string, unknown>; ready: boolean } {
    switch (type) {
      case 'electricity': {
        const input: Record<string, unknown> = {
          consumer_id: electricity.consumer_id.trim(),
          service_provider: electricity.service_provider.trim(),
        };
        if (electricity.regMobileNo.trim().length > 0) {
          input.regMobileNo = electricity.regMobileNo.trim();
        }
        return {
          input,
          ready:
            isValidConsumerId(electricity.consumer_id) &&
            electricity.service_provider.trim().length > 0 &&
            isValidOptionalMobile(electricity.regMobileNo),
        };
      }
      case 'png': {
        // BpNo is optional but the gateway forwards it to Protean, which rejects a
        // NULL bp_no ("Invalid type: null (expected string) for /bp_no"). So always
        // send it as a STRING — empty when the applicant leaves it blank.
        const input: Record<string, unknown> = {
          ConsumerId: png.ConsumerId.trim(),
          ServiceProvider: png.ServiceProvider.trim().toUpperCase(),
          BpNo: png.BpNo.trim(),
        };
        return {
          input,
          ready: png.ConsumerId.trim().length > 0 && png.ServiceProvider.trim().length > 0,
        };
      }
      case 'lpg':
        return {
          input: { LpgId: lpg.LpgId.trim() },
          ready: isValidLpgId(lpg.LpgId),
        };
    }
  }

  const { input, ready } = inputFor(activeType);

  function submit(payload: Record<string, unknown>) {
    mutate(
      { stepId: step.id, input: payload, catalogueEntryId: activeBindingId || undefined },
      {
        onSuccess: (data) => {
          const updated = data.steps.find((s) => s.id === step.id);
          if (!updated) return;
          if (['Passed', 'Review', 'Failed'].includes(updated.status)) return;
          // status Pending → technical provider failure → advance to the next
          // enabled connection type (visible priority fallback), same as Bank / ID.
          const next = types[activeIdx + 1];
          if (next) {
            setFallbackNote(tryAnotherMethodNote(`your ${UTILITY_TYPE_LABEL[next.type]}`));
            setActiveIdx(activeIdx + 1);
          } else {
            toast.error(CHECK_FAILED_TOAST);
            setFallbackNote(RETRY_EXHAUSTED_NOTE);
            setActiveIdx(0);
          }
        },
        onError: (error) => {
          if (isSessionExpiredError(error)) {
            setExpired(true);
            return;
          }
          toast.error('Could not reach the server. Please try again.');
        },
      },
    );
  }

  // DEV: one-click scenario for the ACTIVE connection type.
  function runTestCase(testCase: string) {
    submit({ ...input, test_case: testCase });
  }

  return (
    <div className="flex flex-1 flex-col gap-4">
      {import.meta.env.DEV && (
        <UtilityBillDevSimulator type={activeType} onRun={runTestCase} isPending={isPending} />
      )}

      {fallbackNote && (
        <div className="rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2 text-xs text-[#92400E]">
          {fallbackNote}
        </div>
      )}

      {activeType === 'electricity' && (
        <ElectricityScreen
          values={electricity}
          onChange={(p) => setElectricity((v) => ({ ...v, ...p }))}
          errors={{
            consumer_id:
              electricity.consumer_id.length > 0 && !isValidConsumerId(electricity.consumer_id)
                ? 'Enter a valid 9–13 character consumer ID.'
                : undefined,
            regMobileNo:
              electricity.regMobileNo.length > 0 && !isValidOptionalMobile(electricity.regMobileNo)
                ? 'Enter a valid 10-digit mobile number.'
                : undefined,
          }}
        />
      )}
      {activeType === 'png' && (
        <PngScreen values={png} onChange={(p) => setPng((v) => ({ ...v, ...p }))} />
      )}
      {activeType === 'lpg' && (
        <LpgScreen
          values={lpg}
          onChange={(p) => setLpg((v) => ({ ...v, ...p }))}
          errors={{
            LpgId:
              lpg.LpgId.length > 0 && !isValidLpgId(lpg.LpgId)
                ? 'Enter a valid 17-digit LPG ID.'
                : undefined,
          }}
        />
      )}

      <button
        type="button"
        onClick={() => ready && submit(input)}
        disabled={isPending || !ready}
        style={{
          backgroundColor: 'var(--brand-btn-fill, var(--color-primary))',
          color: 'var(--brand-btn-text, #ffffff)',
          borderColor: 'var(--brand-btn-stroke, var(--color-primary))',
          borderRadius: 'var(--brand-btn-radius, 0.75rem)',
        }}
        className="mt-auto flex w-full items-center justify-center gap-2 border py-3 text-[15px] font-semibold transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
      >
        {isPending && <Loader2 size={15} className="animate-spin" />}
        Continue
      </button>
    </div>
  );
}

function FailureScreen({ reason }: { reason?: string | null }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-4 text-center">
      <div className="mb-6 flex h-[110px] w-[110px] items-center justify-center rounded-full bg-[#DC2626]">
        <X size={60} strokeWidth={3} color="white" />
      </div>
      <h2 className="mb-2 text-[length:var(--text-20)] font-semibold leading-tight text-[#DC2626]">
        Utility bill verification failed
      </h2>
      <p className="text-[length:var(--text-14)] text-[#666666]">
        {reason ?? 'Your connection could not be verified. This session has been rejected.'}
      </p>
    </div>
  );
}
